import { appStore, IEvent, MAX_INT32_VALUE, MessageBus } from '@tom-ui/utils'
import {
  BlockConfigAreaRequest,
  CreateFlexibleBlockRequest,
  CreateYardBlockRequest,
  DeleteConfigAreaRequest,
  LimitStackHeightConfigAreaRequest,
  ReeferPlugConfigAreaRequest,
  ReeferPlugDirection,
  RenameBayRequest,
  RenameRowRequest,
  SpaceBetweenBaysConfigAreaRequest,
  UpdateFlexibleBlockRequest,
  UpdateYardBlockLayoutPositionRequest,
  UpdateYardBlockRequest,
  YardBlockDto,
} from '@yard/app/api'
import { EventTypes } from '@yard/messages/eventsTypes'
import { DeleteResponse } from '@yard/messages/models/delete-response.model'
import { yardConfigService } from '@yard/services/yard-config.service'
import { yardBlockService } from '@yard/services/yardBlock.service'
import { computed, makeObservable } from 'mobx'
import { getBlockIdForJourneyIds } from '@yard/utils/journey.util'
import { BaseEntityStore } from './base/BaseEntityStore'

export class YardBlockStore extends BaseEntityStore<YardBlockDto> {
  private isFetched = false
  private readonly disposers: Array<() => void> = []

  onBlockDimensionsChanged: ((blockId: string) => void) | null = null

  constructor(private readonly messageBus: MessageBus) {
    super()
    makeObservable(this, {
      yardBlocks: computed,
    })

    this.disposers.push(
      this.messageBus.subscribeEvent<YardBlockDto>(
        EventTypes.YardBlockUpsertedEvent,
        this.handleUpsertedEvent,
      ),
      this.messageBus.subscribeEvent<DeleteResponse>(
        EventTypes.YardBlockDeletedEvent,
        this.handleDeletedEvent,
      ),
    )
  }

  dispose(): void {
    this.disposers.forEach(d => d())
    this.disposers.length = 0
  }

  async fetchByIds(ids: string[]) {
    const missingIds = ids.filter(id => !this.yardBlocks[id])
    if (missingIds.length === 0) return

    const uniqueIds = Array.from(new Set(missingIds))

    const yardBlocks = await yardBlockService.getYardBlocks(uniqueIds)
    this.updateStoreItems(yardBlocks)
  }

  async loadYardBlocks() {
    if (this.isFetched) return
    try {
      const yardBlocks = await yardBlockService.getYardBlocks()
      this.updateStoreItems(yardBlocks)
      this.isFetched = true
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  async createYardBlock(createCommand: CreateYardBlockRequest) {
    await yardBlockService.create(createCommand)
  }

  async updateYardBlock(updateCommand: UpdateYardBlockRequest) {
    await yardBlockService.update(updateCommand)
  }

  async renameBay(id: string, bayIndex: number, name: string) {
    const cmd: RenameBayRequest = { id, bayIndex, name }
    await yardBlockService.renameBay(cmd)
  }

  async renameRow(id: string, rowIndex: number, name: string) {
    const cmd: RenameRowRequest = { id, rowIndex, name }
    await yardBlockService.renameRow(cmd)
  }

  async insertBayLeft(blockId: string, bayIndex: number) {
    await yardBlockService.insertBayBefore({ blockId, bayIndex })

    //Remove once SignalR is implemented
    const yardBlockToBeUpdated = this.yardBlocks[blockId]

    if (yardBlockToBeUpdated) {
      const updatedBayNames = [...yardBlockToBeUpdated.bayNames]
      updatedBayNames.splice(bayIndex, 0, `Bay ${bayIndex + 1}`)

      this.updateStoreItem(
        {
          ...yardBlockToBeUpdated,
          bayNames: updatedBayNames,
          maxBays: yardBlockToBeUpdated.maxBays + 1,
        },
        blockId,
      )
    }
  }

  async insertBayRight(blockId: string, bayIndex: number) {
    await yardBlockService.insertBayAfter({ blockId, bayIndex })

    //Remove once SignalR is implemented
    const yardBlockToBeUpdated = this.yardBlocks[blockId]

    if (yardBlockToBeUpdated) {
      const updatedBayNames = [...yardBlockToBeUpdated.bayNames]
      updatedBayNames.splice(bayIndex + 1, 0, `Bay ${bayIndex + 2}`)

      this.updateStoreItem(
        {
          ...yardBlockToBeUpdated,
          bayNames: updatedBayNames,
          maxBays: yardBlockToBeUpdated.maxBays + 1,
        },
        blockId,
      )
    }
  }

  async insertRowUp(blockId: string, rowIndex: number) {
    await yardBlockService.insertRowBefore({ blockId, rowIndex })

    //Remove once SignalR is implemented
    const yardBlockToBeUpdated = this.yardBlocks[blockId]

    if (yardBlockToBeUpdated) {
      const updatedRowNames = [...yardBlockToBeUpdated.rowNames]
      updatedRowNames.splice(rowIndex, 0, `Row ${rowIndex + 1}`)
      this.updateStoreItem(
        {
          ...yardBlockToBeUpdated,
          rowNames: updatedRowNames,
          maxRows: yardBlockToBeUpdated.maxRows + 1,
        },
        blockId,
      )
    }
  }

  async insertRowDown(blockId: string, rowIndex: number) {
    await yardBlockService.insertRowAfter({ blockId, rowIndex })

    //Remove once SignalR is implemented
    const yardBlockToBeUpdated = this.yardBlocks[blockId]

    if (yardBlockToBeUpdated) {
      const updatedRowNames = [...yardBlockToBeUpdated.rowNames]
      updatedRowNames.splice(rowIndex + 1, 0, `Row ${rowIndex + 2}`)
      this.updateStoreItem(
        {
          ...yardBlockToBeUpdated,
          rowNames: updatedRowNames,
          maxRows: yardBlockToBeUpdated.maxRows + 1,
        },
        blockId,
      )
    }
  }

  async deleteBay(blockId: string, bayIndex: number) {
    await yardBlockService.deleteBay({ blockId, bayIndex })

    //Remove once SignalR is implemented
    const yardBlockToBeUpdated = this.yardBlocks[blockId]

    if (yardBlockToBeUpdated) {
      const updatedBayNames = [...yardBlockToBeUpdated.bayNames]
      updatedBayNames.splice(bayIndex, 1)
      this.updateStoreItem(
        {
          ...yardBlockToBeUpdated,
          bayNames: updatedBayNames,
          maxBays: yardBlockToBeUpdated.maxBays - 1,
        },
        blockId,
      )
    }
  }

  async deleteRow(blockId: string, rowIndex: number) {
    await yardBlockService.deleteRow({ blockId, rowIndex })

    //Remove once SignalR is implemented
    const yardBlockToBeUpdated = this.yardBlocks[blockId]

    if (yardBlockToBeUpdated) {
      const updatedRowNames = [...yardBlockToBeUpdated.rowNames]
      updatedRowNames.splice(rowIndex, 1)
      this.updateStoreItem(
        {
          ...yardBlockToBeUpdated,
          rowNames: updatedRowNames,
          maxRows: yardBlockToBeUpdated.maxRows - 1,
        },
        blockId,
      )
    }
  }

  async limitStackHeightConfigArea(
    blockId: string,
    minBay: number,
    maxBay: number,
    minRow: number,
    maxRow: number,
    stackHeight: number,
    description?: string,
  ) {
    const cmd: LimitStackHeightConfigAreaRequest = {
      blockId,
      minBay,
      maxBay,
      minRow,
      maxRow,
      stackHeight,
      description,
    }
    await yardConfigService.limitStackHeightConfigArea(cmd)
  }

  async blockConfigArea(command: BlockConfigAreaRequest) {
    await yardConfigService.blockConfigArea(command)
  }

  async deleteConfigArea(command: DeleteConfigAreaRequest) {
    await yardConfigService.deleteConfigArea(command)
  }

  async reeferPlugConfigArea(blockId: string, bay: number, direction: ReeferPlugDirection) {
    const cmd: ReeferPlugConfigAreaRequest = {
      blockId,
      minBay: bay,
      maxBay: bay,
      minRow: 0,
      maxRow: MAX_INT32_VALUE,
      reeferPlugDirection: direction,
    }
    await yardConfigService.reeferPlugConfigArea(cmd)
  }

  async spaceBetweenBaysConfigArea(blockId: string, bay: number, widthInMeters: number) {
    const cmd: SpaceBetweenBaysConfigAreaRequest = {
      blockId,
      bay,
      widthInMeters,
    }
    await yardConfigService.spaceBetweenBaysConfigArea(cmd)
  }

  async createFlexibleBlock(createCommand: CreateFlexibleBlockRequest) {
    await yardBlockService.createFlexibleBlock(createCommand)
  }

  async updateFlexibleBlock(updateCommand: UpdateFlexibleBlockRequest) {
    await yardBlockService.updateFlexibleBlock(updateCommand)
  }

  async updateLayoutPosition(updateLayoutPositionCommand: UpdateYardBlockLayoutPositionRequest) {
    await appStore.triggerRequestWithoutLoader(() =>
      yardBlockService.updateLayoutPosition(updateLayoutPositionCommand),
    )
  }

  async deleteYardBlock(id: string) {
    await yardBlockService.delete(id)
  }

  async getBlockNamesByJourneyIds(journeyIds: number[], isForPlannedPosition?: boolean) {
    const positions = await getBlockIdForJourneyIds(journeyIds, isForPlannedPosition)

    await this.fetchByIds(positions.filter(p => p.blockId).map(p => p.blockId!))

    return positions.map(({ journeyId, blockId }) => {
      const block = blockId ? this.yardBlocks[blockId] : undefined
      return {
        journeyId,
        position: block?.name ?? '',
      }
    })
  }

  get yardBlocks(): Record<string, YardBlockDto> {
    return this.items.reduce(
      (acc, block) => {
        acc[block.id] = block
        return acc
      },
      {} as Record<string, YardBlockDto>,
    )
  }

  private handleUpsertedEvent = (event: IEvent<YardBlockDto>) => {
    const response = event.payload
    if (!response) return

    const existing = this.yardBlocks[response.id]
    const accessPoints = response.accessPoints?.length
      ? response.accessPoints
      : (existing?.accessPoints ?? [])
    this.updateStoreItem({ ...response, accessPoints }, response.id)

    if (
      existing &&
      (existing.maxBays !== response.maxBays ||
        existing.maxRows !== response.maxRows ||
        existing.configAreas?.length !== response.configAreas?.length)
    ) {
      this.onBlockDimensionsChanged?.(response.id)
    }
  }

  private handleDeletedEvent = (event: IEvent<DeleteResponse>) => {
    const response = event.payload
    if (!response) return
    this.deleteStoreItem(response.id)
  }
}
